// web/src/lib/storage.ts — העלאה והורדה מ-Supabase Storage (28.09 · כלי ההערות v2 · הגירה 092).
// 🔑 בלי supabase-js המלא (ראו supabase.ts): שתי קריאות REST בלבד, באסימון של המשתמשת המחוברת.
// 🔑 הדלי פרטי. המדיניות במסד (092): מעלה רק צוות, לתיקייה {tenant_id}/{auth uid}/ · רואה מי שהעלתה או מנהלת-על.
//    ⛔ קישור ציבורי — רק כתובת חתומה לשעה.
import { config } from "./config";
import { supabase } from "./supabase";

async function session() {
  const { data } = await supabase.auth.getSession();
  if (!data.session) throw new Error("צריך להיכנס מחדש.");
  return data.session;
}

function safeName(name: string) {
  const dot = name.lastIndexOf(".");
  const ext = dot > 0 ? name.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 8) : "";
  return ext ? `file.${ext}` : "file";
}

/** מעלה קובץ לתיקייה של המשתמשת ומחזיר את הנתיב. ⚠️ שם הקובץ המקורי נשמר ברשומה, ⛔ בנתיב (עברית ורווחים). */
export async function uploadFeedbackFile(tenantId: string, blob: Blob, originalName: string): Promise<string> {
  const s = await session();
  const path = `${tenantId}/${s.user.id}/${crypto.randomUUID()}-${safeName(originalName)}`;
  const res = await fetch(`${config.supabaseUrl}/storage/v1/object/feedback-files/${path}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${s.access_token}`,
      apikey: config.supabaseKey,
      "Content-Type": blob.type || "application/octet-stream",
      "x-upsert": "false",
    },
    body: blob,
  });
  if (!res.ok) throw new Error(res.status === 413 ? "הקובץ גדול מדי (עד 15MB)." : "ההעלאה לא הצליחה. אפשר לנסות שוב.");
  return path;
}

/** כתובת חתומה לשעה — לתצוגה ולהורדה. */
export async function signedUrl(path: string): Promise<string> {
  const s = await session();
  const res = await fetch(`${config.supabaseUrl}/storage/v1/object/sign/feedback-files/${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${s.access_token}`, apikey: config.supabaseKey, "Content-Type": "application/json" },
    body: JSON.stringify({ expiresIn: 3600 }),
  });
  if (!res.ok) throw new Error("הקובץ לא זמין.");
  const j = (await res.json()) as { signedURL?: string; signedUrl?: string };
  const rel = j.signedURL ?? j.signedUrl ?? "";
  return rel.startsWith("http") ? rel : `${config.supabaseUrl}/storage/v1${rel}`;
}

/** תמונה גדולה → JPEG עד 1600px (כמו ברשגד: "תמונות מוקטנות במכשיר"). קובץ שאינו תמונה — כמו שהוא. */
export async function shrinkImage(file: Blob, max = 1600, quality = 0.85): Promise<Blob> {
  if (!file.type.startsWith("image/") || file.type === "image/gif" || file.type === "image/svg+xml") return file;
  const bmp = await createImageBitmap(file).catch(() => null);
  if (!bmp) return file;
  const scale = Math.min(1, max / Math.max(bmp.width, bmp.height));
  if (scale === 1 && file.size < 900_000) return file;
  const c = document.createElement("canvas");
  c.width = Math.round(bmp.width * scale);
  c.height = Math.round(bmp.height * scale);
  c.getContext("2d")!.drawImage(bmp, 0, 0, c.width, c.height);
  return await new Promise<Blob>((ok) => c.toBlob((b) => ok(b ?? file), "image/jpeg", quality));
}

// 🆕 01.10 — שותפים פעימה ב (הגירה 097): קבצי השותפות (חשבוניות · אישורי ניכוי מס) בדלי partner-files.
// 🔑 אותו דפוס בדיוק: מעלים לתיקייה {tenant}/{auth uid}/, והשרת רושם דרך פונקציה שמוודאת שהקובץ קיים.
export async function uploadPartnerFile(tenantId: string, blob: Blob, originalName: string): Promise<string> {
  const s = await session();
  const path = `${tenantId}/${s.user.id}/${crypto.randomUUID()}-${safeName(originalName)}`;
  const res = await fetch(`${config.supabaseUrl}/storage/v1/object/partner-files/${path}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${s.access_token}`, apikey: config.supabaseKey,
      "Content-Type": blob.type || "application/octet-stream", "x-upsert": "false",
    },
    body: blob,
  });
  if (!res.ok) throw new Error(res.status === 413 ? "הקובץ גדול מדי (עד 15MB)." : "ההעלאה לא הצליחה. אפשר לנסות שוב.");
  return path;
}

/** כתובת חתומה לשעה בדלי כלשהו (partner-files). */
export async function signedUrlIn(bucket: string, path: string): Promise<string> {
  const s = await session();
  const res = await fetch(`${config.supabaseUrl}/storage/v1/object/sign/${bucket}/${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${s.access_token}`, apikey: config.supabaseKey, "Content-Type": "application/json" },
    body: JSON.stringify({ expiresIn: 3600 }),
  });
  if (!res.ok) throw new Error("הקובץ לא זמין.");
  const j = (await res.json()) as { signedURL?: string; signedUrl?: string };
  const rel = j.signedURL ?? j.signedUrl ?? "";
  return rel.startsWith("http") ? rel : `${config.supabaseUrl}/storage/v1${rel}`;
}

// 🆕 01.10 — שותפים פעימות ג+ד (הגירה 098): העלאה לדלי כלשהו (partner-files של השותפה · marketing-files של הצוות),
// וכתובת ציבורית לחומרי השיווק (הדלי ציבורי לקריאה — חומר שיווק נועד להפצה).
export async function uploadTo(bucket: "partner-files" | "marketing-files", tenantId: string, blob: Blob, originalName: string): Promise<string> {
  const s = await session();
  const path = `${tenantId}/${s.user.id}/${crypto.randomUUID()}-${safeName(originalName)}`;
  const res = await fetch(`${config.supabaseUrl}/storage/v1/object/${bucket}/${path}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${s.access_token}`, apikey: config.supabaseKey,
      "Content-Type": blob.type || "application/octet-stream", "x-upsert": "false",
    },
    body: blob,
  });
  if (!res.ok) throw new Error(res.status === 413 ? "הקובץ גדול מדי." : "ההעלאה לא הצליחה. אפשר לנסות שוב.");
  return path;
}

export function publicUrl(bucket: string, path: string): string {
  return `${config.supabaseUrl}/storage/v1/object/public/${bucket}/${path}`;
}

// 🆕 02.10 — מאגר המדיה (הגירה 104): דלי פרטי media, תיקייה {tenant}/{auth uid}/. עד 50MB.
// 🔑 העלאה ⇒ רישום בשרת (בודק שהקובץ קיים) ⇒ מזהה. ההורדה — קישור חתום שהשרת נותן (⛔ חתימה מהדפדפן).
// 🔑 תמונה מוקטנת לפני ההעלאה (shrinkImage) — תמונת שער/פרופיל לא צריכה 8MB.
export type MediaFile = { id: string; filename?: string | null; kind?: string | null; mime_type?: string | null;
                          size_bytes?: number | null; url?: string | null };

export async function uploadMedia(tenantId: string, blob: Blob, originalName: string): Promise<string> {
  const s = await session();
  const path = `${tenantId}/${s.user.id}/${crypto.randomUUID()}-${safeName(originalName)}`;
  const res = await fetch(`${config.supabaseUrl}/storage/v1/object/media/${path}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${s.access_token}`, apikey: config.supabaseKey,
      "Content-Type": blob.type || "application/octet-stream", "x-upsert": "false",
    },
    body: blob,
  });
  if (!res.ok) throw new Error(res.status === 413 ? "הקובץ גדול מדי (עד 50MB)." : "ההעלאה לא הצליחה. אפשר לנסות שוב.");
  return path;
}

/** מעלה ורושם. who = "staff" (ספרייה · חומרים · שער) או "me" (תמונת פרופיל · קובץ מטלה). */
export async function addMediaFile(file: File, tenantId: string, who: "staff" | "me"): Promise<MediaFile> {
  const { st } = await import("../api/client");
  const blob = await shrinkImage(file);
  const name = blob === file ? file.name : file.name.replace(/\.[^.]+$/, "") + ".jpg";
  const path = await uploadMedia(tenantId, blob, name);
  const body = { path, file_name: name, mime_type: blob.type || file.type || null };
  const r = who === "staff" ? await st.POST("/studio/media", { body }) : await st.POST("/me/media", { body });
  if (!r.data) throw new Error("הקובץ עלה אבל לא נרשם. אפשר לנסות שוב.");
  return r.data as MediaFile;
}

const UNITS = ["B", "KB", "MB", "GB"];
/** 1536 → "1.5KB" */
export function fileSize(n: number | null | undefined): string {
  if (n == null) return "";
  let v = n, i = 0;
  while (v >= 1024 && i < UNITS.length - 1) { v /= 1024; i++; }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)}${UNITS[i]}`;
}

export const KIND_LABEL: Record<string, string> = {
  image: "תמונה", pdf: "PDF", audio: "שמע", video: "וידאו", document: "מסמך", file: "קובץ",
  marketing_image: "תמונה (שיווק)", marketing_video: "וידאו (שיווק)", marketing_text: "טקסט (שיווק)", partner_document: "מסמך שותפה",
};
